import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppFab } from "@/components/landing/WhatsAppFab";
import { IMAGES } from "@/lib/site";
import { WatchBuyerBanner } from "@/components/landing/WatchBuyerBanner";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { productPath, useSEO } from "@/lib/seo";
import { displayTitle, isMontagePrimary } from "@/lib/title";
import { trackViewItemList, trackSelectItem, trackSearch } from "@/lib/analytics";
import { Search, ChevronDown, ChevronLeft, ChevronRight, ShoppingBag, Check, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { WishlistButton } from "@/components/WishlistButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const chipSlug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const imgFallback = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = "/assets/favicon.png";
  e.currentTarget.classList.remove("object-cover", "group-hover:scale-105");
  e.currentTarget.classList.add("object-contain", "p-10", "bg-[#F5F3EC]");
};

const MATERIAL_LABELS = {
  "10K": "10K Gold",
  "14K": "14K Gold",
  "18K+": "18K+ Gold",
  Platinum: "Platinum",
  Silver: "Silver",
  Other: "Other Metals",
};

const FilterGroup = ({ title, testid, children }) => (
  <div data-testid={testid}>
    <h3 className="text-xs uppercase tracking-[0.18em] text-neutral-800 font-bold pb-3 border-b border-[#E8DCC4] mb-3">
      {title}
    </h3>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const FilterRow = ({ label, count, checked, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 py-1.5 group text-left">
    <span
      className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors duration-200 ${
        checked ? "bg-[#C9A227] border-[#C9A227]" : "border-neutral-300 group-hover:border-[#C9A227]"
      }`}
    >
      {checked && <Check size={12} strokeWidth={3} className="text-white" />}
    </span>
    <span className={`text-sm flex-1 ${checked ? "text-neutral-900 font-semibold" : "text-neutral-600"}`}>{label}</span>
    {count !== undefined && <span className="text-xs text-neutral-400">{count.toLocaleString()}</span>}
  </button>
);

export function CatalogBrowser({ forced = {}, heroContent = null }) {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], genders: [], materials: [], colors: [], prices: [], in_stock: 0, subcategories: [], semi_mount: 0, loose_stone: 0, total: 0 });
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [subcatSel, setSubcatSel] = useState((searchParams.get("subcats") || "").split(",").filter(Boolean));
  const [centerStone, setCenterStone] = useState(searchParams.get("semi") || ""); // "", "complete", "semi", "loose"
  const [styleOpen, setStyleOpen] = useState(true);
  const [styleShowAll, setStyleShowAll] = useState(false);
  const [sort, setSort] = useState("");
  const [genderSel, setGenderSel] = useState([]);
  const [materialSel, setMaterialSel] = useState([]);
  const [colorSel, setColorSel] = useState([]);
  const [priceRange, setPriceRange] = useState("");
  const [inStock, setInStock] = useState(false);
  const [stoneSel, setStoneSel] = useState(""); // "", "diamond", "no-diamond"
  const [labSel, setLabSel] = useState(""); // "", "natural", "lab"
  const [shapeSel, setShapeSel] = useState([]);
  const [lengthSel, setLengthSel] = useState([]);
  const [widthSel, setWidthSel] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(() => parseInt(searchParams.get("page"), 10) || 1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [suggestion, setSuggestion] = useState(null);
  const [fuzzyOnly, setFuzzyOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const cart = useCart();

  const toggle = (setter, value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPage(1);
  };

  const activeCount =
    (category ? 1 : 0) + subcatSel.length + (centerStone ? 1 : 0) + genderSel.length + materialSel.length + colorSel.length + (priceRange ? 1 : 0) + (inStock ? 1 : 0)
    + (stoneSel ? 1 : 0) + (labSel ? 1 : 0) + shapeSel.length + lengthSel.length + widthSel.length;

  const clearFilters = () => {
    setCategory("");
    setSubcatSel([]);
    setCenterStone("");
    setGenderSel([]);
    setMaterialSel([]);
    setColorSel([]);
    setPriceRange("");
    setInStock(false);
    setStoneSel("");
    setLabSel("");
    setShapeSel([]);
    setLengthSel([]);
    setWidthSel([]);
    setPage(1);
  };

  const filterParams = () => ({
    q: forced.q || debouncedQ || undefined,
    category: forced.category || category || undefined,
    subcats: forced.subcats?.length ? forced.subcats.join(",") : subcatSel.length ? subcatSel.join(",") : undefined,
    semi_mount: centerStone === "semi" ? true : centerStone === "complete" ? false : undefined,
    loose_stone: forced.loose_stone === true ? true : undefined,
    genders: forced.genders?.length ? forced.genders.join(",") : genderSel.length ? genderSel.join(",") : undefined,
    materials: materialSel.length ? materialSel.join(",") : undefined,
    colors: colorSel.length ? colorSel.join(",") : undefined,
    price: forced.price || priceRange || undefined,
    in_stock: inStock || undefined,
    lab_grown: forced.lab_grown || undefined,
    nameplate: forced.nameplate || undefined,
    new_days: forced.new_days || undefined,
    stone: stoneSel || undefined,
    lab: labSel || undefined,
    shapes: shapeSel.length ? shapeSel.join(",") : undefined,
    lengths: lengthSel.length ? lengthSel.join(",") : undefined,
    widths: widthSel.length ? widthSel.join(",") : undefined,
    sort: sort || forced.sort || undefined,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Paginated URLs (?page=N) stay in the address bar so page 2+ are real,
  // crawlable, shareable URLs — and the browser back/forward buttons work.
  const reqSeq = useRef(0);
  const gridTopRef = useRef(null);
  const firstGridRun = useRef(true);
  const pageHref = (n) => {
    const sp = new URLSearchParams(searchParams);
    if (n > 1) sp.set("page", String(n));
    else sp.delete("page");
    const s = sp.toString();
    return s ? `?${s}` : "?";
  };

  // On page / filter / sort change, scroll smoothly to just above the first
  // card (owner bug report 2026-10-31: paging left the viewport at the bottom).
  // Deliberately NOT tied to debouncedQ so typing in the hero search box never
  // yanks the scroll; the initial mount keeps its own scroll-to-top.
  useEffect(() => {
    if (firstGridRun.current) { firstGridRun.current = false; return; }
    const el = gridTopRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 176;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category, subcatSel, centerStone, genderSel, materialSel, colorSel,
      priceRange, inStock, stoneSel, labSel, shapeSel, lengthSel, widthSel, sort]);

  useEffect(() => {
    const fromUrl = parseInt(searchParams.get("page"), 10) || 1;
    if (fromUrl !== page) setPage(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const fromUrl = parseInt(searchParams.get("page"), 10) || 1;
    if (page === fromUrl) return;
    const sp = new URLSearchParams(searchParams);
    if (page > 1) sp.set("page", String(page));
    else sp.delete("page");
    const s = sp.toString();
    navigate(s ? `?${s}` : "?", { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // Grid loads first; facets load in parallel (non-blocking) and land when
  // ready. The sidebar shows its previous state (or skeleton on first visit)
  // so a slow facets round-trip never holds the product cards back.
  useEffect(() => {
    // Owner rule (2026-10-29): no request for fewer than 3 characters or a bare
    // karat/metal token ("10k", "14k", "sterling") until a second word is typed.
    const qTrim = (debouncedQ || "").trim();
    if (qTrim && (qTrim.length < 3 || /^(\d{1,2}kt?|sterling)$/i.test(qTrim))) return;
    // Race guard (live bug 2026-10-29: "10k wedding band" flipped to chains):
    // each keystroke pause fires a request; with 2-8s engine latencies they
    // return out of order, and the last arrival used to win the grid — an
    // earlier intermediate query ("10k" -> SKU path -> chain SKUs) could
    // overwrite the correct results. Only the newest request may render.
    const myReq = ++reqSeq.current;
    setLoading(true);
    axios
      .get(`${API}/products`, { params: { ...filterParams(), page, limit: 24 } })
      .then((r) => {
        if (myReq !== reqSeq.current) return;
        setProducts(r.data.items);
        setPages(r.data.pages);
        setTotal(r.data.total);
        setSuggestion(r.data.suggestion || null);
        setFuzzyOnly(Boolean(r.data.fuzzy_only));
        const listName = debouncedQ
          ? `Search: ${debouncedQ}`
          : category || "All Jewelry";
        if (r.data.items.length) trackViewItemList(r.data.items, listName);
        if (debouncedQ) trackSearch(debouncedQ, r.data.total);
      })
      .catch(() => {})
      .finally(() => { if (myReq === reqSeq.current) setLoading(false); });
    axios
      .get(`${API}/products/facets`, { params: filterParams() })
      .then((r) => { if (myReq === reqSeq.current) setFacets(r.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, category, subcatSel, centerStone, genderSel, materialSel, colorSel, priceRange, inStock, stoneSel, labSel, shapeSel, lengthSel, widthSel, page, sort]);

  return (
    <main data-testid="catalog-page">
      <Header />

      <section className="relative overflow-hidden pt-[164px] pb-14 lg:pt-[196px] lg:pb-16" data-testid="catalog-hero">
        <img
          src="/assets/texas-glow.jpg"
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] lg:w-[1400px] opacity-20 pointer-events-none select-none [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_72%)]"
        />
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {heroContent || (
              <>
                <span className="font-display text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-6 block">
                  The Catalog
                </span>
                <h1
                  data-testid="catalog-heading"
                  className="font-display text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-neutral-900 leading-[1.1]"
                >
                  Tens of thousands of pieces, <span className="text-[#C9A227]">ready to order</span>
                </h1>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-2xl mx-auto">
                  Browse our full catalog. Anything you love ships insured directly to you —
                  or choose ship-to-showroom pickup at Katy, Copperas Cove or Killeen at checkout.
                </p>
              </>
            )}
          </motion.div>

          {!forced.q && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-xl mx-auto relative"
          >
            <Search size={18} strokeWidth={1.5} className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="catalog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search chains, rings, earrings, SKUs..."
              className="w-full bg-white border border-[#E8DCC4] rounded-full pl-12 pr-5 py-3.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            />
          </motion.div>
          )}
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="catalog-grid-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
          <button
            data-testid="catalog-filters-toggle"
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden mb-6 inline-flex items-center gap-2 rounded-full border border-[#C9A227] text-[#C9A227] text-sm font-semibold px-5 py-2.5"
          >
            <SlidersHorizontal size={15} strokeWidth={1.5} /> Filters {activeCount > 0 && `(${activeCount})`}
          </button>

          <div className="lg:grid lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-10">
            <aside
              data-testid="catalog-filters"
              className={`${showFilters ? "block" : "hidden"} lg:block mb-10 lg:mb-0 space-y-8`}
            >
              {activeCount > 0 && (
                <button
                  data-testid="filters-clear"
                  onClick={clearFilters}
                  className="text-sm font-semibold text-[#C9A227] hover:text-[#A8871B] transition-colors duration-300"
                >
                  Clear all filters ({activeCount})
                </button>
              )}

              {!forced.subcats && (forced.category || category) && facets.subcategories?.length > 0 && (
                <div data-testid="filter-style">
                  <button
                    data-testid="filter-style-toggle"
                    onClick={() => setStyleOpen(!styleOpen)}
                    className="w-full flex items-center justify-between pb-3 border-b border-[#E8DCC4] mb-3"
                  >
                    <h3 className="text-xs uppercase tracking-[0.18em] text-neutral-800 font-bold">Style</h3>
                    <ChevronDown
                      size={15}
                      strokeWidth={1.5}
                      className={`text-[#C9A227] transition-transform duration-300 ${styleOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {styleOpen && (
                    <div className="space-y-0.5">
                      {(styleShowAll ? facets.subcategories : facets.subcategories.slice(0, 8)).map((s) => (
                        <FilterRow
                          key={s.name}
                          label={s.name}
                          count={s.count}
                          checked={subcatSel.includes(s.name)}
                          onClick={() => toggle(setSubcatSel, s.name)}
                          testid={`filter-style-${chipSlug(s.name)}`}
                        />
                      ))}
                      {facets.subcategories.length > 8 && (
                        <button
                          data-testid="filter-style-more"
                          onClick={() => setStyleShowAll(!styleShowAll)}
                          className="text-xs font-semibold text-[#C9A227] hover:text-[#A8871B] pt-1.5 transition-colors duration-300"
                        >
                          {styleShowAll ? "Show fewer styles" : `+ ${facets.subcategories.length - 8} more styles`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!forced.category && (
                <FilterGroup title="Category" testid="filter-category">
                  <FilterRow label="All" checked={category === ""} onClick={() => { setCategory(""); setPage(1); }} testid="filter-category-all" />
                  {facets.categories.map((c) => (
                    <FilterRow key={c.name} label={c.name} count={c.count} checked={category === c.name} onClick={() => { setCategory(category === c.name ? "" : c.name); setPage(1); }} testid={`filter-category-${chipSlug(c.name)}`} />
                  ))}
                </FilterGroup>
              )}

              {!forced.subcats && !forced.loose_stone && facets.semi_mount > 0 && (
                <FilterGroup title="Piece Type" testid="filter-center-stone">
                  <FilterRow label="All" checked={centerStone === ""} onClick={() => setCenterStone("")} testid="filter-center-stone-all" />
                  <FilterRow label="Complete jewelry" count={Math.max(0, (facets.total || 0) - facets.semi_mount)} checked={centerStone === "complete"} onClick={() => setCenterStone(centerStone === "complete" ? "" : "complete")} testid="filter-center-stone-complete" />
                  <FilterRow label="Semi-mounts (no center stone)" count={facets.semi_mount} checked={centerStone === "semi"} onClick={() => setCenterStone(centerStone === "semi" ? "" : "semi")} testid="filter-center-stone-semi" />
                </FilterGroup>
              )}

              {!forced.genders && (
                <FilterGroup title="Shop For" testid="filter-gender">
                  {facets.genders.map((g) => (
                    <FilterRow key={g.name} label={g.name} count={g.count} checked={genderSel.includes(g.name)} onClick={() => toggle(setGenderSel, g.name)} testid={`filter-gender-${g.name.toLowerCase()}`} />
                  ))}
                </FilterGroup>
              )}

              {!forced.price && (
                <FilterGroup title="Price" testid="filter-price">
                  {facets.prices.map((pr) => (
                    <FilterRow key={pr.key} label={pr.label} count={pr.count} checked={priceRange === pr.key} onClick={() => { setPriceRange(priceRange === pr.key ? "" : pr.key); setPage(1); }} testid={`filter-price-${pr.key}`} />
                  ))}
                </FilterGroup>
              )}

              <FilterGroup title="Metal" testid="filter-material">
                {facets.materials.map((m) => (
                  <FilterRow key={m.name} label={MATERIAL_LABELS[m.name] || m.name} count={m.count} checked={materialSel.includes(m.name)} onClick={() => toggle(setMaterialSel, m.name)} testid={`filter-material-${m.name.toLowerCase().replace("+", "plus")}`} />
                ))}
              </FilterGroup>

              <FilterGroup title="Gold Color" testid="filter-color">
                {facets.colors.map((c) => (
                  <FilterRow key={c.name} label={c.name} count={c.count} checked={colorSel.includes(c.name)} onClick={() => toggle(setColorSel, c.name)} testid={`filter-color-${chipSlug(c.name)}`} />
                ))}
              </FilterGroup>

              {facets.stone && facets.stone.diamond > 0 && (
                <FilterGroup title="Stone" testid="filter-stone">
                  <FilterRow label="All" checked={stoneSel === ""} onClick={() => { setStoneSel(""); setPage(1); }} testid="filter-stone-all" />
                  <FilterRow label="Diamond" count={facets.stone.diamond} checked={stoneSel === "diamond"} onClick={() => { setStoneSel(stoneSel === "diamond" ? "" : "diamond"); setPage(1); }} testid="filter-stone-diamond" />
                  <FilterRow label="No diamond" count={facets.stone.no_diamond} checked={stoneSel === "no-diamond"} onClick={() => { setStoneSel(stoneSel === "no-diamond" ? "" : "no-diamond"); setPage(1); }} testid="filter-stone-none" />
                </FilterGroup>
              )}

              {facets.lab && facets.lab.lab > 0 && stoneSel !== "no-diamond" && (
                <FilterGroup title="Diamond Origin" testid="filter-lab">
                  <FilterRow label="All" checked={labSel === ""} onClick={() => { setLabSel(""); setPage(1); }} testid="filter-lab-all" />
                  <FilterRow label="Natural" count={facets.lab.natural} checked={labSel === "natural"} onClick={() => { setLabSel(labSel === "natural" ? "" : "natural"); setPage(1); }} testid="filter-lab-natural" />
                  <FilterRow label="Lab-grown" count={facets.lab.lab} checked={labSel === "lab"} onClick={() => { setLabSel(labSel === "lab" ? "" : "lab"); setPage(1); }} testid="filter-lab-lab" />
                </FilterGroup>
              )}

              {facets.shapes && facets.shapes.length > 0 && stoneSel !== "no-diamond" && (
                <FilterGroup title="Stone Shape" testid="filter-shape">
                  {facets.shapes.map((s) => (
                    <FilterRow key={s.name} label={s.name} count={s.count} checked={shapeSel.includes(s.name)} onClick={() => toggle(setShapeSel, s.name)} testid={`filter-shape-${chipSlug(s.name)}`} />
                  ))}
                </FilterGroup>
              )}

              {facets.lengths && facets.lengths.length > 0 && (
                <FilterGroup title="Length" testid="filter-length">
                  {facets.lengths.map((l) => (
                    <FilterRow key={l.name} label={`${l.name}"`} count={l.count} checked={lengthSel.includes(l.name)} onClick={() => toggle(setLengthSel, l.name)} testid={`filter-length-${l.name}`} />
                  ))}
                </FilterGroup>
              )}

              {facets.widths && facets.widths.some((w) => w.count > 0) && (
                <FilterGroup title="Width" testid="filter-width">
                  {facets.widths.filter((w) => w.count > 0).map((w) => (
                    <FilterRow key={w.key} label={w.label} count={w.count} checked={widthSel.includes(w.key)} onClick={() => toggle(setWidthSel, w.key)} testid={`filter-width-${w.key}`} />
                  ))}
                </FilterGroup>
              )}

              <FilterGroup title="Availability" testid="filter-stock">
                <FilterRow label="In stock now" count={facets.in_stock} checked={inStock} onClick={() => { setInStock(!inStock); setPage(1); }} testid="filter-stock-in" />
              </FilterGroup>
            </aside>

            <div ref={gridTopRef}>
              {(category === "Wrist" || (forced.category || "").includes("Wrist") || /watch/i.test(debouncedQ)) && (
                <div className="mb-8">
                  <WatchBuyerBanner testid="catalog-watch-buyer-banner" />
                </div>
              )}
              <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
                <p className="text-sm text-neutral-500" data-testid="catalog-results-count">
                  {loading ? "Loading..." : `${total.toLocaleString()}${typeof total === "number" && total >= 1000 && debouncedQ ? "+" : ""} piece${total === 1 ? "" : "s"}`}
                </p>
                <select
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    setPage(1);
                  }}
                  data-testid="catalog-sort"
                  className="bg-white border border-[#E8DCC4] rounded-sm px-3 py-2 text-sm text-neutral-800 focus:outline-none focus:border-[#C9A227]"
                >
                  <option value="">Sort: Featured</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="newest">Newest</option>
                </select>
              </div>

          {!loading && products.length === 0 && (
            <div className="text-neutral-500 py-16 text-center" data-testid="catalog-empty">
              <p>Nothing matched — try a different search, or ask us and we'll source it for you.</p>
              {suggestion && (
                <p className="mt-4 text-sm">
                  Did you mean{" "}
                  <button
                    onClick={() => { setQ(suggestion); }}
                    className="text-[#C9A227] font-semibold hover:text-[#A8871B] underline underline-offset-4"
                    data-testid="catalog-suggestion"
                  >
                    {suggestion}
                  </button>
                  ?
                </p>
              )}
            </div>
          )}

          {fuzzyOnly && products.length > 0 && (
            <p className="text-sm text-neutral-500 mb-4" data-testid="fuzzy-only-notice">
              No exact matches for &ldquo;{debouncedQ}&rdquo;, showing close matches.
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-8" data-testid="catalog-skeleton" aria-busy="true">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="bg-white border border-[#E8DCC4] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-pulse">
                  <div className="w-full h-[180px] sm:h-[220px] lg:h-[260px] bg-[#F1EBDC]" />
                  <div className="p-4 lg:p-5 space-y-2">
                    <div className="h-2.5 w-16 bg-[#F1EBDC] rounded" />
                    <div className="h-4 w-3/4 bg-[#F1EBDC] rounded" />
                    <div className="h-3.5 w-24 bg-[#F1EBDC] rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-8" data-testid="catalog-grid">
            {products.map((p) => (
              <Link
                key={p.sku}
                to={productPath(p)}
                onClick={() => trackSelectItem(p, debouncedQ ? `Search: ${debouncedQ}` : category || "All Jewelry")}
                data-testid={`catalog-card-${p.sku}`}
                className="group block text-left cursor-pointer bg-white border border-[#E8DCC4] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(201,162,39,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-500"
              >
                <div className="overflow-hidden relative">
                  <img
                    src={(p.nameplate && isMontagePrimary(p) ? p.np_hero_image : null) || p.image_url || IMAGES.jewelry}
                    alt={displayTitle(p)}
                    loading="lazy"
                    onError={imgFallback}
                    className="w-full h-[180px] sm:h-[220px] lg:h-[260px] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  <WishlistButton sku={p.sku} testid={`catalog-wishlist-${p.sku}`} className="absolute top-3 right-3 w-9 h-9 bg-white/90 border border-[#E8DCC4] shadow-sm" />
                  <span
                    data-testid={`catalog-stock-${p.sku}`}
                    className={`absolute top-3 left-3 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.12em] font-semibold ${
                      p.nameplate || p.qty_avail > 0 ? "bg-[#0D0D0D]/85 text-[#E8CC6E]" : "bg-white/90 text-neutral-600"
                    }`}
                  >
                    {p.nameplate ? "Made to Order" : p.qty_avail > 0 ? "In stock" : "Order-in"}
                  </span>
                </div>
                <div className="p-4 lg:p-5">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#C9A227] font-semibold">{p.category}</p>
                  <h3 className="text-sm lg:text-base font-semibold text-neutral-900 mt-1 leading-snug">
                    {p.nameplate && p.family_title ? p.family_title : displayTitle(p)}
                  </h3>
                  {p.nameplate && (
                    <p className="text-[11px] text-neutral-500 mt-0.5" data-testid={`catalog-np-options-${p.sku}`}>
                      {(p.family_count || 0) > 1 ? `${p.family_count} metal options · made to order` : "Made to order"}
                    </p>
                  )}
                  {p.qty_avail > 0 && p.qty_avail <= 3 && !p.variant_axis && !p.nameplate && (
                    <p className="text-[11px] font-semibold text-[#A8871B] mt-1" data-testid={`catalog-lowstock-${p.sku}`}>
                      Only {p.qty_avail} left in stock
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <div>
                      {p.nameplate && (p.np_mode || "quote") === "quote" ? (
                        <>
                          <p className="text-sm font-semibold text-neutral-800" data-testid={`catalog-price-${p.sku}`}>
                            Starting at {money(p.family_min_price || p.retail_price)}
                          </p>
                          <p className="text-[10px] text-neutral-400 mt-0.5">Final price confirmed after review</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-neutral-800" data-testid={`catalog-price-${p.sku}`}>
                            {p.nameplate && (p.family_count || 0) > 1 && p.family_min_price
                              ? `From ${money(p.family_min_price)}`
                              : money(p.retail_price)}
                          </p>
                          <p className="text-[10px] text-neutral-400 mt-0.5">Affirm &amp; Klarna at checkout</p>
                        </>
                      )}
                    </div>
                    {!p.nameplate && (
                      <button
                        type="button"
                        data-testid={`catalog-add-${p.sku}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          cart.add(p);
                          toast.success("Added to your cart");
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-xs font-semibold px-4 py-2.5 lg:py-1.5 hover:from-[#F0D988] hover:to-[#D4AF37] transition-colors duration-300"
                      >
                        <ShoppingBag size={13} strokeWidth={1.5} /> Add
                      </button>
                    )}
                    {p.nameplate && (p.np_mode || "quote") === "quote" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A227] text-[#A8871B] text-xs font-semibold px-4 py-2.5 lg:py-1.5" data-testid={`catalog-quote-cta-${p.sku}`}>
                        Request a Quote
                      </span>
                    )}
                    {p.nameplate && p.np_mode === "fixed" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A227] text-[#A8871B] text-xs font-semibold px-4 py-2.5 lg:py-1.5" data-testid={`catalog-personalize-${p.sku}`}>
                        Personalize
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          )}

          {pages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-4" data-testid="catalog-pagination">
              <Link
                data-testid="catalog-prev"
                to={pageHref(page - 1)}
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) setPage(page - 1);
                }}
                aria-disabled={page <= 1}
                tabIndex={page <= 1 ? -1 : 0}
                className={`inline-flex items-center gap-1 rounded-full border border-[#E8DCC4] px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  page <= 1
                    ? "opacity-40 pointer-events-none text-neutral-700"
                    : "text-neutral-700 hover:border-[#C9A227] hover:text-[#C9A227]"
                }`}
              >
                <ChevronLeft size={16} strokeWidth={1.5} /> Prev
              </Link>
              <span className="text-sm text-neutral-500" data-testid="catalog-page-indicator">
                Page {page} of {pages}
              </span>
              <Link
                data-testid="catalog-next"
                to={pageHref(page + 1)}
                onClick={(e) => {
                  e.preventDefault();
                  if (page < pages) setPage(page + 1);
                }}
                aria-disabled={page >= pages}
                tabIndex={page >= pages ? -1 : 0}
                className={`inline-flex items-center gap-1 rounded-full border border-[#E8DCC4] px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  page >= pages
                    ? "opacity-40 pointer-events-none text-neutral-700"
                    : "text-neutral-700 hover:border-[#C9A227] hover:text-[#C9A227]"
                }`}
              >
                Next <ChevronRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}

export default function CatalogPage() {
  // Faceted-navigation rule: the canonical is ALWAYS the bare /catalog URL, so
  // filter/sort/search combinations (?metal= ?price= ?sort= ?q= …) never become
  // indexable duplicate URLs. Strategic categories get clean /collections/* routes.
  useSEO({
    title: "Shop the Catalog — Gold & Diamond Jewelry | Gold Time Jewelry",
    description:
      "Browse tens of thousands of gold chains, diamond rings, earrings, bracelets, name plates and more. In-stock pieces ship insured nationwide — or ship to our Katy, Copperas Cove or Killeen showrooms for pickup.",
    canonicalPath: "/catalog",
  });
  return <CatalogBrowser />;
}
